import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, AlertCircle, Info, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDismissedUIElements } from '@/hooks/useDismissedUIElements';

interface VendorOnboardingBannerProps {
  hasBasicVerification: boolean;
  hasRequestedVerification: boolean;
  stripeConfigured: boolean;
  hasSubmittedBid: boolean;
  profileCompletionPercentage: number;
  missingItems: string[];
}

export const VendorOnboardingBanner: React.FC<VendorOnboardingBannerProps> = ({
  hasBasicVerification,
  hasRequestedVerification,
  stripeConfigured,
  hasSubmittedBid,
  profileCompletionPercentage,
  missingItems,
}) => {
  const navigate = useNavigate();
  const { isDismissed, dismiss } = useDismissedUIElements('vendor_onboarding_success');

  // Show success banner only if all requirements met and user has submitted at least one bid
  // and hasn't dismissed it yet
  if (hasBasicVerification && stripeConfigured && hasSubmittedBid && !isDismissed) {
    return (
      <Alert className="border-success bg-success/10">
        <CheckCircle2 className="h-5 w-5 text-success" />
        <div className="flex-1">
          <AlertTitle className="text-success">Your Profile is Complete!</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            You're all set to bid on RFQs and accept purchase orders. Good luck!
          </AlertDescription>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => dismiss()}
          className="ml-auto"
        >
          Dismiss
        </Button>
      </Alert>
    );
  }

  // Don't show banner if all requirements are met and bid submitted
  if (hasBasicVerification && stripeConfigured) {
    return null;
  }

  // Show warning if only Stripe is missing (profile/verification done)
  if (hasBasicVerification && !stripeConfigured) {
    return (
      <Alert className="border-warning bg-warning/10">
        <Info className="h-5 w-5 text-warning" />
        <div className="flex-1">
        <AlertTitle className="text-warning">Action Required: Configure Payment Options</AlertTitle>
        <AlertDescription className="text-muted-foreground mt-2">
          Your profile is verified! You must configure your payment options through Stripe Connect 
          before you can submit bids on RFQs.
        </AlertDescription>
          <Button
            size="sm"
            variant="default"
            className="mt-3"
            onClick={() => navigate('/settings/account')}
          >
            <Settings className="h-4 w-4 mr-2" />
            Set Up Payments
          </Button>
        </div>
      </Alert>
    );
  }

  // Show pending verification state
  if (!hasBasicVerification && hasRequestedVerification) {
    return (
      <Alert className="border-warning bg-warning/10">
        <Info className="h-5 w-5 text-warning" />
        <div className="flex-1">
          <AlertTitle className="text-warning">Verification Pending</AlertTitle>
          <AlertDescription className="text-muted-foreground mt-2 space-y-3">
            <p>
              Your verification request is being reviewed by our team. This typically takes 1-2 business days.
            </p>
            {!stripeConfigured && (
              <p>
                While you wait, you can set up your payment options to be ready when verification is approved.
              </p>
            )}
            {!stripeConfigured && (
              <Button
                size="sm"
                variant="default"
                className="mt-2"
                onClick={() => navigate('/settings/account')}
              >
                <Settings className="h-4 w-4 mr-2" />
                Set Up Payments
              </Button>
            )}
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  // Show critical alert if verification or profile incomplete
  return (
    <Alert className="border-destructive bg-destructive/10">
      <AlertCircle className="h-5 w-5 text-destructive" />
      <div className="flex-1">
        <AlertTitle className="text-destructive">Welcome! Complete Your Vendor Setup</AlertTitle>
        <AlertDescription className="text-muted-foreground mt-2 space-y-3">
          <p>
            To start bidding on RFQs and winning jobs, you need to complete the following steps:
          </p>
          
          <div className="space-y-2">
            {missingItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <div className="h-5 w-5 rounded-full border-2 border-muted-foreground flex items-center justify-center text-xs">
                  {index + 1}
                </div>
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Profile Completion:</span>
              <span className="text-muted-foreground">{profileCompletionPercentage}%</span>
            </div>
            <Progress value={profileCompletionPercentage} className="h-2" />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => navigate('/profile/vendor?tab=user')}
            >
              Complete Profile
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/settings/account')}
            >
              <Settings className="h-4 w-4 mr-2" />
              Set Up Payments
            </Button>
          </div>
        </AlertDescription>
      </div>
    </Alert>
  );
};
